import { Button } from '@/components/ui/button'
import { SearchField } from '@/components/patterns/SearchField'
import type { Filter, Register } from '../query/types'
import { describeSort } from './quickConditions'
import { QuickFilters } from './QuickFilters'
import type { FilterOptions } from './useFilterOptions'

interface FilterBarProps {
  register: Register
  filter: Filter
  /** The search term in the URL. */
  q: string
  /** The sort the list has now, e.g. "name" or "-last_activity_at". */
  sort: string
  /** How many records match; null while the first page loads. */
  total: number | null
  options: FilterOptions
  /** Today in Amsterdam as YYYY-MM-DD. */
  today: string
  /** "9 contacten", "1 contact". */
  noun: (count: number) => string
  searchPlaceholder: string
  searchLabel: string
  /** Keep it stable: a new function on every render restarts the search field's debounce. */
  onSearch: (term: string) => void
  onFilterChange: (next: Filter) => void
  onOpenAdvanced: () => void
  onOpenColumns: () => void
}

/**
 * The bar above a list (design §4.2): the search field, the quick filters, "Geavanceerde filters (n)"
 * with the number of conditions in the groups, "Kolommen", and the number of results with the sort
 * once it is not the one by name ("9 contacten · gesorteerd op laatst gewijzigd"). Below 768 px only
 * the search field and the count stay; the filter sheet carries the rest.
 */
export function FilterBar({
  register,
  filter,
  q,
  sort,
  total,
  options,
  today,
  noun,
  searchPlaceholder,
  searchLabel,
  onSearch,
  onFilterChange,
  onOpenAdvanced,
  onOpenColumns,
}: FilterBarProps) {
  const advanced = filter.groups.reduce((sum, group) => sum + group.length, 0)
  const sorted = describeSort(sort, register)
  const result = total === null ? '' : `${noun(total)}${sorted ? ` · gesorteerd op ${sorted}` : ''}`
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={q} onSearch={onSearch} placeholder={searchPlaceholder} label={searchLabel} />
        <QuickFilters register={register} filter={filter} options={options} today={today} onChange={onFilterChange} className="contents max-md:hidden" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="max-md:hidden" onClick={onOpenAdvanced}>
          Geavanceerde filters{advanced > 0 ? ` (${advanced})` : ''}
        </Button>
        <Button type="button" variant="outline" size="sm" className="max-md:hidden" onClick={onOpenColumns}>
          Kolommen
        </Button>
        <p aria-live="polite" className="ml-auto text-sm text-muted-foreground">
          {result}
        </p>
      </div>
    </div>
  )
}
